/**
 * Protected Route Component
 * Wraps routes that require authentication
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

/**
 * ProtectedRoute Component
 * Redirects to login if not authenticated
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles
}) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  // Block rendering until auth state is determined
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#1a1a1a', color: '#a0a0a0' }}>
        <p>Loading session...</p>
      </div>
    );
  }

  // If not authenticated, redirect to the login page
  if (!isAuthenticated && !loading) {
    console.log('[ProtectedRoute] Not authenticated, redirecting to login page...');
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  console.log('[ProtectedRoute] User is authenticated, allowing access to:', location.pathname);

  // Check role-based access if required roles specified
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = user?.roles.some(role =>
      requiredRoles.includes(role)
    );

    if (!hasRequiredRole) {
      return (
        <Navigate
          to="/access-denied"
          replace
        />
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
